import { useCartDrawer, useGlobalSettings } from "global-stores";
import { MinusIcon, PlusIcon } from "icons";
import { ReactProductState } from "product";
import { FC } from "react";
import { StoreApi, UseBoundStore } from "zustand";
import { QuantitySelectorState } from "./cart-drawer";

export const ProductQuantitySelector: FC<{
  useProduct: UseBoundStore<StoreApi<ReactProductState | QuantitySelectorState>>;
  showText?: boolean;
  allowZero?: boolean;
  size?: "sm" | "md";
}> = ({ useProduct, showText = false, allowZero = false, size = "md" }) => {
  const { setQuantity, quantity, formId, selectedVariant, product } = useProduct(
    ({ setQuantity, quantity, formId, selectedVariant, product }) => ({
      product,
      setQuantity,
      quantity,
      formId,
      selectedVariant,
    })
  );
  const {
    product_data__preorder_metafield,
    product_data__preorder_date_metafield,
    product_data__preorder_limit_metafield,
  } = useGlobalSettings(
    ({
      product_data__preorder_metafield,
      product_data__preorder_date_metafield,
      product_data__preorder_limit_metafield,
    }) => ({
      product_data__preorder_metafield,
      product_data__preorder_date_metafield,
      product_data__preorder_limit_metafield,
    })
  );

  const [preorder_ns, preorder_key] = product_data__preorder_metafield.split(".");
  const [date_ns, date_key] = product_data__preorder_date_metafield.split(".");
  const [limit_ns, limit_key] = product_data__preorder_limit_metafield.split(".");

  const pre_order =
    selectedVariant?.metafields?.[preorder_ns]?.[preorder_key] ??
    product?.metafields?.[preorder_ns]?.[preorder_key];

  const pre_order_limit = +(selectedVariant?.metafields?.[limit_ns]?.[limit_key] ?? 0);

  const { routeProtectionProducts } = useCartDrawer(({ routeProtectionProducts }) => ({
    routeProtectionProducts,
  }));

  const routeProtection = routeProtectionProducts.find(
    (block) => +block?.variant_id === +selectedVariant.id
  );

  const isSingleQuantityRouteProtectionItem = !!routeProtection?.limit_quantity;

  const sellPast = selectedVariant?.inventory_policy === "continue";
  const trackInventory = selectedVariant?.inventory_management === "shopify";
  const inventory_quantity = selectedVariant?.inventory_quantity ?? 0;
  const hasInventory = inventory_quantity > 0;

  if (isSingleQuantityRouteProtectionItem) {
    if (quantity !== 1) {
      setQuantity(1);
    }
    return null;
  }
  return (
    <label className={`${size === "sm" ? "text-input--sm p-[4px] border" : "text-input--md w-full"} select-none !rounded !border-theme-text/30`}>
      {showText && <span className="flex-1">Quantity</span>}
      <button
        type="button"
        className="flex items-center justify-center px-1.5 py-1 text-theme-text hf:text-theme-text/80"
        onClick={() => {
          setQuantity(
            Math.max(
              allowZero ? 0 : 1,
              sellPast && pre_order && pre_order_limit
                ? Math.min(quantity - 1, inventory_quantity + pre_order_limit)
                : sellPast || !trackInventory
                ? quantity - 1
                : Math.min(quantity - 1, inventory_quantity)
            )
          );
        }}
      >
        <span className="sr-only">Decrease quantity by 1</span>
        <MinusIcon className="h-2.5 w-2.5" />
      </button>
      <input
        className="w-8 appearance-none bg-inherit text-center outline-none"
        type="number"
        name="quantity"
        min="1"
        value={quantity}
        form={formId}
        onChange={(e) => {
          setQuantity(
            Math.max(
              1,
              sellPast && pre_order && pre_order_limit
                ? Math.min(+e.currentTarget.value, inventory_quantity + pre_order_limit)
                : sellPast || !trackInventory
                ? +e.currentTarget.value
                : Math.min(+e.currentTarget.value, inventory_quantity)
            )
          );
        }}
      />
      <button
        type="button"
        className="flex items-center justify-center px-1.5 py-1 text-theme-text hf:text-theme-text/80"
        onClick={() => {
          setQuantity(
            Math.max(
              1,
              sellPast && pre_order && pre_order_limit
                ? Math.min(quantity + 1, inventory_quantity + pre_order_limit)
                : sellPast || !trackInventory
                ? quantity + 1
                : Math.min(quantity + 1, inventory_quantity)
            )
          );
        }}
      >
        <span className="sr-only">Increase quantity by 1</span>
        <PlusIcon className="h-2.5 w-2.5" />
      </button>
    </label>
  );
};
